import { Link } from "@tanstack/react-router";
import { ChevronDown, ExternalLink, LogOut } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { cn } from "../../lib/utils";
import {
	type NavActionId,
	type NavBadges,
	groupForPath,
	type ResolvedEntry,
	type ResolvedGroup,
	type ResolvedItem,
	resolveActiveHref,
} from "./nav-config";

/**
 * THE INDEX RED RULE.
 *
 * Index red marks the current item as a 3px inset bar and essentially nothing
 * else — never a filled block, which spends its scarcity. It is never used for
 * state (a status is a printed code in status ink; see the status primitives).
 *
 * `w-0.75` is 3px on the spacing scale (0.25rem × 0.75) and `secondary-500`
 * is the index-red token — #D23A1F in `@interioring/design-system`, still the
 * legacy sage until `styles/earthy-legacy.css` is deleted, which is the
 * reskin. Both constants live here so a grep for `secondary-` under
 * `components/layout/` returns exactly these two lines: the rail/tile marker
 * and the bottom-nav slot marker.
 */
export const INDEX_MARK =
	"before:absolute before:inset-y-0 before:left-0 before:w-0.75 before:bg-secondary-500 before:content-['']";

/** The same 3px bar on the top edge, for the mobile bottom-nav slot. */
export const INDEX_MARK_TOP =
	"before:absolute before:inset-x-0 before:top-0 before:h-0.75 before:bg-secondary-500 before:content-['']";

/**
 * A `.label`: caps, tabular, tight. The Job Book's section heads and counts.
 * (The design world's 620 weight and 80% width are Anek Latin axes, which the
 * portal now ships — phase 2.6. This is still the Tailwind scale rather than
 * the `.label` ramp class: switching the rail onto the two axes moves every
 * row's metrics and wants its own visual pass.)
 */
const LABEL = "text-xs font-semibold uppercase tracking-wide tabular-nums";

/**
 * Rows are the dense 32px tier, with the 44px floor kept ONLY under
 * `@media (pointer: coarse)` — a mouse does not need a 44px nav row, and
 * spending 12px of vertical rhythm on one is how the old rail stopped fitting
 * on a screen.
 */
function rowClass(opts: { active: boolean; indent: boolean }): string {
	return cn(
		"group relative flex min-h-8 w-full items-center gap-2 px-3.5 text-left text-sm transition-colors",
		"pointer-coarse:min-h-11",
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
		// Rows TINT on hover: ink at low alpha, so one value is right on every
		// ground in both themes. The −3px lift is reserved for draggable things.
		"hover:bg-foreground-default/7 hover:text-foreground-default",
		opts.indent && "pl-6",
		opts.active
			? cn(
					"bg-foreground-default/7 font-semibold text-foreground-default",
					INDEX_MARK,
				)
			: "text-foreground-muted",
	);
}

/** A count: `.label` tabular, right-aligned, NO PILL. */
function RowCount({ count }: { count: number }) {
	return (
		<span className={cn(LABEL, "ml-auto text-inherit")}>
			{count > 99 ? "99+" : count}
		</span>
	);
}

type RailRowProps = {
	item: ResolvedItem;
	active: boolean;
	count?: number;
	indent: boolean;
	onNavigate: () => void;
	onAction: (action: NavActionId) => void;
};

/**
 * The ONE row renderer.
 *
 * `sidebar.tsx` rendered a nav row six times over — a data-driven map plus
 * five hand-written one-offs (Social Studio, Admin Panel, Marketplace,
 * Feedback, Notifications), each with its own copy of the active/hover/badge
 * classes, and `admin-sidebar.tsx` had two more. All of it is this function.
 * Three element shapes are unavoidable (an internal link, an external anchor
 * and a button are different elements) but there is one class function and one
 * index bar.
 */
function RailRow({
	item,
	active,
	count,
	indent,
	onNavigate,
	onAction,
}: RailRowProps) {
	const className = rowClass({ active, indent });
	const label = <span className="truncate">{item.name}</span>;
	const badge =
		count !== undefined && count > 0 ? <RowCount count={count} /> : null;

	if (item.kind === "action") {
		return (
			<button
				type="button"
				className={className}
				onClick={() => {
					onNavigate();
					onAction(item.action);
				}}
			>
				{label}
				{badge}
			</button>
		);
	}

	if (item.kind === "external") {
		return (
			<a
				href={item.href}
				target="_blank"
				rel="noopener noreferrer"
				className={className}
				onClick={onNavigate}
			>
				{label}
				<ExternalLink
					className="ml-auto size-3.5 flex-none text-foreground-subtle"
					aria-hidden="true"
				/>
				<span className="sr-only">(opens in a new tab)</span>
			</a>
		);
	}

	return (
		<Link
			to={item.href}
			onClick={onNavigate}
			aria-current={active ? "page" : undefined}
			className={className}
		>
			{label}
			{badge}
		</Link>
	);
}

/** A hairline. Not a box. */
function RailDivider() {
	return (
		<hr className="mx-3.5 my-1 h-0 border-0 border-t border-border-default" />
	);
}

/**
 * Which groups are folded is resolved in two layers: an explicit choice the
 * user made by clicking a head (remembered across visits), and underneath it
 * the group that owns the current page. Only the choices are stored — the
 * active group is DERIVED — so a week of browsing does not slowly re-expand
 * the whole rail. This is the one thing the old admin rail got right; it is
 * carried over verbatim in behaviour.
 */
type GroupChoices = Record<string, boolean>;

function readChoices(storageKey: string): GroupChoices {
	try {
		const raw = localStorage.getItem(storageKey);
		const parsed: unknown = raw ? JSON.parse(raw) : {};
		return parsed && typeof parsed === "object" ? (parsed as GroupChoices) : {};
	} catch {
		return {};
	}
}

function writeChoices(storageKey: string, choices: GroupChoices): void {
	try {
		localStorage.setItem(storageKey, JSON.stringify(choices));
	} catch {
		// Private mode or storage disabled: the rail still folds, it just stops
		// remembering.
	}
}

/** Initials for the footer identity block. Two words at most. */
export function initialsOf(name: string | null | undefined): string {
	const words = (name ?? "").trim().split(/\s+/).filter(Boolean).slice(0, 2);
	if (words.length === 0) return "?";
	return words.map((word) => word.charAt(0).toUpperCase()).join("");
}

/** A row's identity, for React keys. */
function itemKey(item: ResolvedItem): string {
	return item.kind === "action" ? item.action : item.href;
}

/**
 * Keys for a flat list that includes dividers, which have no identity of
 * their own: a divider is named after the row above it, so the key is stable
 * across re-renders and across role changes (an array index is neither).
 */
function keyedEntries(
	entries: ResolvedEntry[],
): { key: string; entry: ResolvedEntry }[] {
	let previous = "top";
	return entries.map((entry) => {
		if (entry.kind === "divider") {
			return { key: `divider-after-${previous}`, entry };
		}
		previous = entry.kind === "group" ? entry.name : itemKey(entry);
		return { key: previous, entry };
	});
}

const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export type NavRailIdentity = {
	name: string;
	/** The second line: `Owner · Live`, `Platform admin`. */
	meta: string;
};

export type NavRailProps = {
	/** Already role-resolved — see `resolveNav`. */
	entries: ResolvedEntry[];
	currentPath: string;
	badges?: NavBadges;
	/** Where the folds are remembered. One key per rail. */
	storageKey: string;
	ariaLabel: string;
	identity: NavRailIdentity;
	onSignOut: () => void;
	onAction?: (action: NavActionId) => void;
	/** Drawer state. On `lg` and up the rail is static and always visible. */
	isOpen: boolean;
	onClose: () => void;
	/** Rendered above the footer — push/install prompts, and nothing else. */
	children?: ReactNode;
};

export function NavRail({
	entries,
	currentPath,
	badges,
	storageKey,
	ariaLabel,
	identity,
	onSignOut,
	onAction,
	isOpen,
	onClose,
	children,
}: NavRailProps) {
	const railRef = useRef<HTMLDivElement>(null);
	const activeHref = resolveActiveHref(entries, currentPath);
	const activeGroup = groupForPath(entries, currentPath);
	const [choices, setChoices] = useState<GroupChoices>(() =>
		readChoices(storageKey),
	);
	// Folding exists to make the DESKTOP rail fit on one screen. On a phone the
	// drawer covers the screen anyway, and a fold there only adds a tap before
	// every destination — so the drawer always shows everything.
	const isMobile = useIsMobile();

	// Arriving in a group you had folded drops that one choice, so the page you
	// are on is never hidden. Other choices are untouched.
	useEffect(() => {
		if (!activeGroup) return;
		setChoices((previous) => {
			if (previous[activeGroup] !== false) return previous;
			const { [activeGroup]: _dropped, ...rest } = previous;
			return rest;
		});
	}, [activeGroup]);

	useEffect(() => {
		writeChoices(storageKey, choices);
	}, [storageKey, choices]);

	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [isOpen, onClose]);

	// Focus the first destination when the drawer opens.
	useEffect(() => {
		if (!isOpen) return;
		railRef.current?.querySelector<HTMLAnchorElement>("nav a")?.focus();
	}, [isOpen]);

	const counts: NavBadges = badges ?? {};

	const isGroupOpen = (group: ResolvedGroup): boolean => {
		const choice = choices[group.name];
		if (choice !== undefined) return choice;
		// Sections stand open; a submenu (`Settings ▾`) stays shut until it owns
		// the page, because its five rows are not the daily work.
		return group.style === "section" || group.name === activeGroup;
	};

	const toggleGroup = (group: ResolvedGroup) =>
		setChoices((previous) => ({
			...previous,
			[group.name]: !isGroupOpen(group),
		}));

	const countFor = (item: ResolvedItem): number | undefined =>
		item.kind === "action" || item.kind === "external" || !item.badge
			? undefined
			: counts[item.badge];

	const renderItem = (item: ResolvedItem, indent: boolean) => (
		<RailRow
			key={itemKey(item)}
			item={item}
			active={item.kind === "link" && item.href === activeHref}
			count={countFor(item)}
			indent={indent}
			onNavigate={onClose}
			onAction={(action) => onAction?.(action)}
		/>
	);

	const renderGroup = (group: ResolvedGroup) => {
		const open = isMobile || isGroupOpen(group);
		const listId = `nav-rail-group-${slug(group.name)}`;
		const rolledUp = group.items.reduce(
			(total, item) => total + (countFor(item) ?? 0),
			0,
		);
		const isSection = group.style === "section";
		return (
			<div key={group.name}>
				<button
					type="button"
					onClick={() => toggleGroup(group)}
					aria-expanded={open}
					aria-controls={listId}
					data-testid={`nav-rail-group-${group.name}`}
					className={
						isSection
							? cn(
									LABEL,
									"flex min-h-8 w-full items-center gap-1.5 px-3.5 pt-1.5 pb-0.5 text-left text-foreground-subtle transition-colors",
									"pointer-coarse:min-h-11",
									"hover:text-foreground-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
									group.name === activeGroup && "text-foreground-default",
								)
							: rowClass({ active: false, indent: false })
					}
				>
					<span className="truncate">{group.name}</span>
					<span className="ml-auto flex items-center gap-1.5">
						{/* Folded, the group's counts roll up to its head, so a fold
						    never hides the fact that something needs attention. */}
						{!open && rolledUp > 0 && <span className={LABEL}>{rolledUp}</span>}
						<ChevronDown
							aria-hidden="true"
							className={cn(
								"size-3.5 flex-none text-foreground-subtle transition-transform duration-200",
								!open && "-rotate-90",
							)}
						/>
					</span>
					{!open && (
						<span className="sr-only">
							collapsed, {group.items.length} items
							{rolledUp > 0 ? `, ${rolledUp} need attention` : ""}
						</span>
					)}
				</button>
				<div id={listId} hidden={!open}>
					{group.items.map((item) => renderItem(item, isSection))}
				</div>
			</div>
		);
	};

	return (
		<div
			ref={railRef}
			className={cn(
				// 232px — the rail width in the mockup (`--rail-w`).
				"fixed inset-y-0 left-0 z-50 flex w-58 flex-col border-r border-border-default bg-background-subtle transition-transform duration-300 ease-in-out lg:static lg:translate-x-0",
				isOpen ? "translate-x-0" : "-translate-x-full",
			)}
		>
			<nav
				aria-label={ariaLabel}
				className="flex flex-1 flex-col overflow-y-auto py-2"
			>
				{keyedEntries(entries).map(({ key, entry }) => {
					if (entry.kind === "divider") return <RailDivider key={key} />;
					if (entry.kind === "group") return renderGroup(entry);
					return renderItem(entry, false);
				})}

				<div className="flex-1" />
				{children}
			</nav>

			{/* The footer identity block: initials, name, `Owner · Live`. */}
			<div className="border-t border-border-default px-3.5 py-3">
				<div className="flex items-center gap-2">
					<span
						aria-hidden="true"
						className={cn(
							LABEL,
							"flex size-8 flex-none items-center justify-center rounded-full bg-primary-100 text-primary-700",
						)}
					>
						{initialsOf(identity.name)}
					</span>
					<span className="min-w-0 flex-1">
						<span className="block truncate text-sm font-medium text-foreground-default">
							{identity.name}
						</span>
						<span className={cn(LABEL, "block truncate text-foreground-muted")}>
							{identity.meta}
						</span>
					</span>
					<button
						type="button"
						onClick={onSignOut}
						title="Sign out"
						className="flex size-6 flex-none items-center justify-center text-foreground-subtle transition-colors hover:text-foreground-default pointer-coarse:size-11"
					>
						<LogOut className="size-4" aria-hidden="true" />
						<span className="sr-only">Sign out</span>
					</button>
				</div>
			</div>
		</div>
	);
}
